/**
 * ScopeStage — V102-3-4 CMMC Scope page (ADR-0057).
 *
 * Mounted via the `cmmc-scope` panel id (programs/manifests.ts →
 * shell.panels.scope). Three-column layout at ≥1024px:
 *   col 1 — Suggested, unassigned (categorizer + dismiss sheet)
 *   col 2 — In boundary (open workbench + collapsed category rows)
 *   col 3 — Live SSP §2.1 preview, drafts tinted amber
 *
 * Role gate: owner/admin or program_lead for commit/dismiss writes; any
 * member reads. A write-less viewer gets inline "ask an admin" affordances.
 *
 * Fixture mode (import.meta.env.DEV + VITE_FIXTURES): `?scope=` picks one of
 * four shapes (empty/suggested/mixed/done); production reads the live APIs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState } from '@aigovops/design-system';
import { useKeyboardShortcuts, ShortcutHelpOverlay, type Shortcut } from '../../lib/use-keyboard-shortcuts.js';
import {
  renderSspSection21,
  SCOPE_CATEGORIES,
  type AssetSuggestion,
  type ProgramCard,
  type ScopeCategoryKey,
  type ScopeDraft,
  type ScopedAsset,
  type SspParagraph,
} from '@aigovops/core';
import { cmmcPeriod } from '../../lib/cmmc-model';
import { useDialogMobileOnly } from '../../lib/use-dialog-mobile-only.js';
import { AskAdmin } from '../../components/AskAdmin';
import {
  listAssetSuggestions,
  listPeriodScope,
  removeScopeAsset,
  setScopeCategory,
  setScopeDraft,
} from '../../lib/api';
import { scopeFixture, scopeFixtureState } from '../../dev/fixtures-cmmc-scope';
import './scope-stage.css';

type Props = {
  slug: string;
  program: string;
  card: ProgramCard;
  canManage: boolean;
  prepareHref: string;
};

const CATEGORY_LABEL: Record<ScopeCategoryKey, string> = {
  cui: 'CUI Asset', security_protection: 'Security Protection',
  contractor_risk_managed: 'Contractor Risk Managed', specialized: 'Specialized Asset', out_of_scope: 'Out-of-Scope',
};
const CATEGORY_TREATMENT_CHIP: Record<ScopeCategoryKey, 'assessed' | 'documented' | 'excluded'> = {
  cui: 'assessed', security_protection: 'assessed',
  contractor_risk_managed: 'documented', specialized: 'documented', out_of_scope: 'excluded',
};
const CAT_KEYS = SCOPE_CATEGORIES.map((c) => c.key as ScopeCategoryKey);

const CAT_CLASS: Record<ScopeCategoryKey, string> = {
  cui: 'cat-cui', security_protection: 'cat-sec', contractor_risk_managed: 'cat-crma',
  specialized: 'cat-spc', out_of_scope: 'cat-oos',
};

const USE_FIXTURES = import.meta.env.DEV && import.meta.env.VITE_FIXTURES === '1';

function timeHHMM(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function ScopeStage({ slug, program, card, canManage, prepareHref }: Props) {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const liveRef = useRef<HTMLDivElement>(null);
  const queueRef = useRef<HTMLHeadingElement>(null);
  // Drafts are Partial — category stays undefined until the user picks one (CUI default was dangerous).
  const [drafts, setDrafts] = useState<Record<string, Partial<ScopeDraft>>>({});
  const [draftSavedAt, setDraftSavedAt] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [savedAtIso, setSavedAtIso] = useState<string>(new Date().toISOString());
  const [dismissFor, setDismissFor] = useState<AssetSuggestion | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissedKeys, setDismissedKeys] = useState<Record<string, { name: string; reason: string }>>({});

  const period = cmmcPeriod(card);
  const periodId = period?.period_id ?? '';
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);

  // Fixture branch (dev harness).
  const fx = useMemo(() => (USE_FIXTURES ? scopeFixture(scopeFixtureState()) : null), [sp]);

  const suggestionsQuery = useQuery({
    queryKey: ['scope-suggestions', slug],
    queryFn: () => listAssetSuggestions(slug),
    enabled: !fx,
    staleTime: 30_000,
  });
  const scopeQuery = useQuery({
    queryKey: ['scope-period', slug, program, periodId],
    queryFn: () => listPeriodScope(slug, program, periodId),
    enabled: !fx && !!periodId,
    staleTime: 30_000,
  });

  const suggestions: AssetSuggestion[] = fx ? fx.suggestions : suggestionsQuery.data?.suggestions ?? [];
  const scope: ScopedAsset[] = fx ? fx.scope : scopeQuery.data?.assets ?? [];
  const liveSuggestions = suggestions.filter((s) => !dismissedKeys[s.suggestion_key]);
  const dismissed = fx ? fx.dismissed : Object.entries(dismissedKeys).map(([key, v]) => ({
    suggestion_key: key, name: v.name, reason: v.reason, dismissed_by: 'you', dismissed_at: new Date().toISOString(),
  }));

  // Only complete drafts (both fields set) reach the SSP preview — partial picks don't render.
  const completeDrafts = useMemo(() => {
    const out: Record<string, ScopeDraft> = {};
    for (const [k, d] of Object.entries(drafts)) {
      if (d && d.category && typeof d.rationale === 'string' && d.rationale.trim()) {
        out[k] = { category: d.category, rationale: d.rationale };
      }
    }
    return out;
  }, [drafts]);
  const section = useMemo(() => renderSspSection21(scope, completeDrafts), [scope, completeDrafts]);

  // By-category group for col 2
  const byCategory = useMemo(() => {
    const g: Record<ScopeCategoryKey, ScopedAsset[]> = {
      cui: [], security_protection: [], contractor_risk_managed: [], specialized: [], out_of_scope: [],
    };
    for (const s of scope) {
      const k = s.membership.category as ScopeCategoryKey;
      if (k in g) g[k].push(s);
    }
    return g;
  }, [scope]);

  const [openCategory, setOpenCategory] = useState<ScopeCategoryKey>('cui');
  const [openSuggestion, setOpenSuggestion] = useState<string | null>(null);

  const unassignedCount = liveSuggestions.length;
  const categorizedCount = scope.length;
  const totalCount = categorizedCount + unassignedCount;
  const ctaReady = unassignedCount === 0 && categorizedCount > 0;
  const bannerDone = fx ? fx.banner_done : ctaReady;
  const announce = useCallback((msg: string) => { if (liveRef.current) liveRef.current.textContent = msg; }, []);

  // V1035-3-2: keyboard shortcuts (? help, Cmd/Ctrl+Enter commit)
  const commitScope = useCallback(() => {
    if (ctaReady && canManage) {
      announce('Scope marked ready — advancing to Prepare');
      navigate(prepareHref);
    }
  }, [ctaReady, canManage, prepareHref, navigate]);
  const scopeShortcuts: Shortcut[] = useMemo(() => [
    { key: '?', label: 'Show keyboard shortcuts', action: () => setShowShortcutHelp(true) },
    { key: 'Enter', modifier: 'cmd', label: 'Commit scope (advance to Prepare)', action: commitScope },
  ], [commitScope]);
  useKeyboardShortcuts(scopeShortcuts);

  // --- Debounced draft autosave --------------------------------------------
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  function updateDraft(key: string, patch: Partial<ScopeDraft> & { assetId?: string }) {
    setDrafts((prev) => {
      const current: Partial<ScopeDraft> = prev[key] ?? {};
      const next: Partial<ScopeDraft> = { ...current, ...patch };
      delete (next as { assetId?: string }).assetId;
      const out = { ...prev, [key]: next };
      // Only hit the server for drafts of REAL assets (already in boundary) with BOTH fields set.
      if (patch.assetId && !fx && next.category && typeof next.rationale === 'string') {
        if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
        debounceRef.current[key] = setTimeout(() => {
          setSaveState('saving'); announce('Saving…');
          setScopeDraft(slug, patch.assetId!, { period_id: periodId, category: next.category!, rationale: next.rationale! })
            .then(() => {
              const now = new Date().toISOString();
              setDraftSavedAt((d) => ({ ...d, [key]: now }));
              setSavedAtIso(now); setSaveState('saved'); announce('Saved');
            })
            .catch(() => { setSaveState('error'); announce('Changes failed — Retry'); });
        }, 500);
      } else {
        setDraftSavedAt((d) => ({ ...d, [key]: new Date().toISOString() }));
      }
      return out;
    });
  }

  const queryClient = useQueryClient();

  async function commit(suggestionOrAssetId: { assetId: string; name: string }, draftKey: string) {
    const d = drafts[draftKey];
    if (!d || !d.category || !(d.rationale ?? '').trim()) return;
    const cat = d.category;
    try {
      if (!fx) {
        await setScopeCategory(slug, program, periodId, suggestionOrAssetId.assetId, {
          category: cat, rationale: d.rationale || null,
        });
        await queryClient.invalidateQueries({ queryKey: ['scope-period', slug, program, periodId] });
      }
      setDrafts(({ [draftKey]: _, ...rest }) => rest);
      setSavedAtIso(new Date().toISOString()); setSaveState('saved'); announce(`${suggestionOrAssetId.name} added to ${CATEGORY_LABEL[cat]}`);
    } catch { setSaveState('error'); announce('Changes failed — Retry'); }
  }

  async function dismiss() {
    if (!dismissFor) return;
    const key = dismissFor.suggestion_key;
    setDismissedKeys((prev) => ({ ...prev, [key]: { name: dismissFor.name, reason: dismissReason } }));
    setDismissFor(null); setDismissReason('');
    announce(`${dismissFor.name} dismissed`);
  }

  async function restoreDismissed(suggestionKey: string) {
    setDismissedKeys(({ [suggestionKey]: _, ...rest }) => rest);
    announce('Restored to suggestions');
  }

  const [removeFor, setRemoveFor] = useState<{ assetId: string; name: string } | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  async function confirmRemove() {
    if (!removeFor || !removeReason.trim()) return;
    const { assetId, name } = removeFor;
    try {
      if (!fx) {
        await removeScopeAsset(slug, program, periodId, assetId, removeReason);
        await queryClient.invalidateQueries({ queryKey: ['scope-period', slug, program, periodId] });
      }
      announce(`${name} removed from boundary`);
    } catch { setSaveState('error'); announce('Changes failed — Retry'); }
    setRemoveFor(null); setRemoveReason('');
  }
  function removeFromBoundary(assetId: string, name: string) {
    setRemoveFor({ assetId, name });
  }

  // Focus-on-chip (per prototype): scroll + focus the FIRST unassigned card, not the heading.
  const firstSuggestionRef = useRef<HTMLElement>(null);
  const scrollToQueue = () => {
    const target = firstSuggestionRef.current ?? queueRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target?.focus();
  };

  // Role gate — a read-only member sees everything but every write affordance becomes an AskAdmin.
  if (!canManage && !card) {
    return <EmptyState title="Scope is managed by the CMMC program lead" />;
  }

  // V1035-3-1 item 5: EmptyState using the DS component (no connectors, no boundary)
  if ((totalCount === 0 && !fx) || fx?.state === 'empty') {
    return (
      <EmptyState
        title="Start your assessment boundary"
        description="Connect your identity provider to discover 60–80% of what belongs in scope. Entra ID, Okta, AWS, Azure, GCP, Intune, and Jamf are supported."
        action={
          <Button variant="primary" onClick={() => (window.location.href = `/w/${slug}/app/settings/integrations`)}>
            Connect your first source
          </Button>
        }
      />
    );
  }

  const savedBannerLabel = saveState === 'saving' ? 'Saving…'
    : saveState === 'error' ? 'Changes failed — Retry'
    : 'Saved';

  return (
    <div className="gp-scope">
      {/* V1035-3-1 item 4: PageHeader with Export and Help */}
      <div className="gp-scope-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)' }}>Scope</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="ghost" onClick={() => window.open('https://gacpro.io/docs/cmmc-scope', '_blank')}>Help</Button>
            <Button variant="secondary" onClick={() => {
              const blob = new Blob([JSON.stringify(section, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `scope-${slug}-${program}.json`; a.click();
              URL.revokeObjectURL(url);
            }}>Export</Button>
          </div>
        </div>
      </div>

      {/* Live region */}
      <div ref={liveRef} role="status" aria-live="polite" aria-atomic="true" className="sr-only" />

      {/* Sticky save strip — the sr-only `liveRef` above is the single announcer; this strip is visual only. */}
      <div className="gp-scope-save" aria-hidden="true">
        <span className={`gp-pulse${saveState === 'error' ? ' error' : ''}`} />
        <span className="gp-save-label">{savedBannerLabel}</span>
        <span className="gp-save-ts" title={savedAtIso}>at {timeHHMM(savedAtIso)}</span>
      </div>

      {/* Glance tile */}
      <section className="gp-scope-glance" aria-labelledby="glance-h">
        <h2 id="glance-h" className="sr-only">Scope status</h2>
        <div className="gp-glance-row">
          <div className="gp-glance-count">
            <strong>{categorizedCount}</strong>
            <span>of {totalCount} categorized</span>
          </div>
          <div className="gp-glance-progress" role="img" aria-label={`${categorizedCount} of ${totalCount} assets categorized`}>
            <div className="gp-prog-track"><div className="gp-prog-fill" style={{ width: totalCount ? `${Math.round((categorizedCount / totalCount) * 100)}%` : '0%' }} /></div>
            <div className="gp-prog-stack">
              {CAT_KEYS.map((k) => {
                const n = byCategory[k].length;
                const pct = totalCount ? Math.round((n / totalCount) * 100) : 0;
                return pct > 0 ? <span key={k} className={CAT_CLASS[k]} style={{ width: `${pct}%` }} title={`${CATEGORY_LABEL[k]}: ${n}`} /> : null;
              })}
            </div>
          </div>
          {unassignedCount > 0 ? (
            <button className="gp-chip-count" onClick={scrollToQueue} aria-label={`${unassignedCount} waiting on you — scroll to the suggested queue`}>
              {unassignedCount} waiting on you →
            </button>
          ) : null}
        </div>
        {/* Category legend — numeric breakdown users can't read from the bar alone. */}
        {categorizedCount > 0 ? (
          <div className="gp-prog-legend" aria-hidden="true">
            {CAT_KEYS.map((k) => byCategory[k].length > 0
              ? <span key={k}><i className={CAT_CLASS[k]} /> {CATEGORY_LABEL[k]} {byCategory[k].length}</span>
              : null)}
          </div>
        ) : null}
      </section>

      {/* Primary CTA row — the reason is a visible-to-SR hint (3.3.1 / 4.1.2) and visibly muted when inert. */}
      <div className="gp-scope-cta-row">
        <Button variant="primary"
          className={!ctaReady || !canManage ? 'gp-cta-disabled' : undefined}
          aria-disabled={!ctaReady || !canManage ? true : undefined}
          aria-describedby="cta-reason"
          title={ctaReady ? 'Advance to Prepare' : 'Assign all suggested assets to a category to continue.'}
          onClick={() => {
            if (!ctaReady || !canManage) return;
            announce('Scope marked ready — advancing to Prepare');
            navigate(prepareHref);
          }}>
          {ctaReady ? 'Advance to Prepare →' : 'Mark Scope ready for Prepare →'}
        </Button>
        <span id="cta-reason" className="sr-only">
          {ctaReady ? 'Ready to advance to Prepare.' : `Assign all ${unassignedCount} suggested assets to a category to continue.`}
        </span>
      </div>

      <main className="gp-scope-grid">
        {/* Col 1 — Suggested */}
        <section className="gp-col-suggested" aria-labelledby="sug-h">
          <h2 id="sug-h" tabIndex={-1} ref={queueRef}>Suggested, unassigned</h2>
          {liveSuggestions.length === 0 ? (
            <p className="gp-dim">Nothing waiting — the queue is clear.</p>
          ) : liveSuggestions.map((sug, idx) => {
            const key = sug.suggestion_key;
            // Default category is UNSELECTED — CUI auto-tagging is the most expensive mistake (flow critic R2).
            const draft = drafts[key];
            const draftCategory = draft?.category;
            const draftRationale = draft?.rationale ?? '';
            const open = openSuggestion === key;
            return (
              <article key={key} ref={idx === 0 ? firstSuggestionRef : undefined} tabIndex={idx === 0 ? -1 : undefined} className={`gp-sug-card${draftCategory ? ` ${CAT_CLASS[draftCategory]}` : ''}`}>
                <header>
                  <div className="gp-sug-head">
                    <div className="gp-sug-name">{sug.name}</div>
                    <div className="gp-dim">
                      <span className="gp-chip-source">{sug.source.kind.replace('_', ' ')}</span>
                      <span>via {sug.discovered_from}</span>
                    </div>
                  </div>
                  <button className="gp-ghost" onClick={() => setOpenSuggestion(open ? null : key)} aria-expanded={open}>{open ? 'Collapse' : 'Categorize'}</button>
                </header>
                {open ? (
                  <>
                    <fieldset className="gp-seg">
                      <legend>Category <span aria-hidden="true" style={{ color: 'var(--band-red)' }}>*</span><span className="sr-only"> (required — pick one)</span></legend>
                      {CAT_KEYS.map((k) => (
                        <label key={k} className={`${CAT_CLASS[k]}${draftCategory === k ? ' sel' : ''}`}>
                          <input type="radio" name={`cat-${key}`} checked={draftCategory === k}
                            onChange={() => updateDraft(key, { category: k })} />
                          <div>
                            <div className="gp-seg-lbl">
                              <span className="gp-cat-dot" /> {CATEGORY_LABEL[k]}
                              <span className={`gp-chip-${CATEGORY_TREATMENT_CHIP[k]}`}>{CATEGORY_TREATMENT_CHIP[k]}</span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </fieldset>
                    <label className="gp-field">
                      <span>Rationale <span aria-hidden="true" style={{ color: 'var(--band-red)' }}>*</span><span className="sr-only"> (required)</span> — quoted in the SSP.</span>
                      <textarea value={draftRationale} aria-required="true" onChange={(e) => updateDraft(key, { rationale: e.currentTarget.value })} />
                      {draftSavedAt[key] ? <span className="gp-dim">Draft saved {timeHHMM(draftSavedAt[key])}</span> : null}
                    </label>
                    <div className="gp-sug-foot">
                      <button className="gp-ghost" onClick={() => setDismissFor(sug)}>Dismiss…</button>
                      {canManage ? (
                        <>
                          <Button variant="primary"
                            className={!draftCategory || !draftRationale.trim() ? 'gp-cta-disabled' : undefined}
                            aria-disabled={!draftCategory || !draftRationale.trim() ? true : undefined}
                            aria-describedby={`add-reason-${key}`}
                            onClick={() => {
                              if (!draftCategory || !draftRationale.trim()) return;
                              commit({ assetId: `pending:${key}`, name: sug.name }, key);
                            }}>
                            Add to boundary
                          </Button>
                          <span id={`add-reason-${key}`} className="sr-only">
                            {!draftCategory ? 'Pick a category to continue.' : !draftRationale.trim() ? 'Add a rationale to continue.' : 'Ready to add.'}
                          </span>
                        </>
                      ) : <AskAdmin slug={slug} compact verb="add to boundary" what="categorizes assets" subject={`GAC Pro: categorize ${sug.name}`} />}
                    </div>
                  </>
                ) : null}
              </article>
            );
          })}

          {dismissed.length > 0 ? (
            <details className="gp-dismissed">
              <summary>Dismissed ({dismissed.length}) <span className="gp-dim" style={{ fontWeight: 400 }}>— kept in this session only; reload will restore</span></summary>
              <ul>
                {dismissed.map((d) => (
                  <li key={d.suggestion_key}>
                    <div>
                      <div className="gp-sug-name">{d.name}</div>
                      <div className="gp-dim">reason: &ldquo;{d.reason}&rdquo; — dismissed by {d.dismissed_by}</div>
                    </div>
                    <button className="gp-ghost" onClick={() => restoreDismissed(d.suggestion_key)}>Restore</button>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>

        {/* Col 2 — In boundary */}
        <section className="gp-col-boundary" aria-labelledby="bnd-h">
          <h2 id="bnd-h">In boundary</h2>
          {bannerDone ? (
            <Alert variant="success">
              <strong>Scope complete — all {categorizedCount} assets categorized.</strong>
              <div className="gp-dim">The SSP §2.1 paragraph is finalized and will be included in the Prepare package.</div>
            </Alert>
          ) : null}
          {CAT_KEYS.map((k) => {
            const rows = byCategory[k];
            const isOpen = openCategory === k;
            if (rows.length === 0 && !isOpen) return null;
            return isOpen ? (
              <section key={k} className={`gp-cat-open ${CAT_CLASS[k]}`} aria-labelledby={`cat-${k}`}>
                <div className="gp-cat-head">
                  <span className="gp-cat-dot" />
                  <h3 id={`cat-${k}`}>{CATEGORY_LABEL[k]}</h3>
                  <span className={`gp-chip-${CATEGORY_TREATMENT_CHIP[k]}`}>{CATEGORY_TREATMENT_CHIP[k]}</span>
                  <span className="gp-count">{rows.length}</span>
                </div>
                <ul>
                  {rows.length === 0 ? <li className="gp-dim">No {CATEGORY_LABEL[k]} assets yet.</li>
                    : rows.map((s) => (
                      <li key={s.asset.asset_id} className="gp-row">
                        <div>
                          <div className="gp-sug-name">{s.asset.name}</div>
                          <div className="gp-dim">{s.asset.tier} · {s.membership.decided_by}</div>
                        </div>
                        {canManage ? (
                          <button className="gp-ghost" onClick={() => removeFromBoundary(s.asset.asset_id, s.asset.name)}>Remove</button>
                        ) : null}
                      </li>
                    ))}
                </ul>
              </section>
            ) : (
              <button key={k} className={`gp-cat-collapsed ${CAT_CLASS[k]}`} onClick={() => setOpenCategory(k)}
                aria-expanded={false} aria-label={`Open ${CATEGORY_LABEL[k]}, ${rows.length} assets`}>
                <span className="gp-cat-dot" aria-hidden="true" />
                <span className="gp-cat-head-inline">
                  <strong>{CATEGORY_LABEL[k]}</strong>
                  <span className={`gp-chip-${CATEGORY_TREATMENT_CHIP[k]}`}>{CATEGORY_TREATMENT_CHIP[k]}</span>
                  <span className="gp-preview">{rows.slice(0, 2).map((r) => r.asset.name).join(', ')}</span>
                </span>
                <span className="gp-count">{rows.length}</span>
              </button>
            );
          })}
        </section>

        {/* Col 3 — SSP preview (V1035-3-1 item 3: headline-first, collapsed by default) */}
        <section className="gp-col-ssp" aria-labelledby="ssp-h">
          <h2 id="ssp-h">Boundary summary</h2>
          <Card>
            {section.paragraphs.map((p: SspParagraph) => (
              <details key={p.category_key} className={`gp-ssp-para ${CAT_CLASS[p.category_key]}`}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="gp-cat-dot" aria-hidden="true" /> {p.category_label} <span className="gp-count">{p.asset_count}</span>
                </summary>
                <div style={{ marginTop: '8px', fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  {p.lines.map((line, i) => typeof line === 'string'
                    ? <span key={i}> {line}</span>
                    : <em key={i} className="gp-ssp-draft"> Draft: &ldquo;{line.text}&rdquo;</em>)}
                </div>
              </details>
            ))}
          </Card>
        </section>
      </main>

      {/* Dismiss sheet (modal dialog — ESC + focus trap + initial focus per 2.1.2 / 4.1.2) */}
      {dismissFor ? (
        <ModalSheet labelledBy="dismiss-h" onClose={() => { setDismissFor(null); setDismissReason(''); }}>
          <h2 id="dismiss-h">Dismiss {dismissFor.name}?</h2>
          <p>This suggestion will move to the Dismissed tray with your reason recorded. Auditors can see the full trail.</p>
          <label className="gp-field">
            <span>Reason <span aria-hidden="true" style={{ color: 'var(--band-red)' }}>*</span><span className="sr-only"> (required)</span></span>
            <textarea autoFocus aria-required="true" value={dismissReason} onChange={(e) => setDismissReason(e.currentTarget.value)} placeholder="e.g. no CUI touch, billing SaaS only" />
          </label>
          <div className="gp-sheet-actions">
            <Button variant="secondary" onClick={() => { setDismissFor(null); setDismissReason(''); }}>Cancel</Button>
            <Button variant="primary" aria-disabled={!dismissReason.trim() ? true : undefined} onClick={dismiss}>Dismiss</Button>
          </div>
        </ModalSheet>
      ) : null}

      {/* V1035-3-2: keyboard shortcut help overlay */}
      {showShortcutHelp ? <ShortcutHelpOverlay shortcuts={scopeShortcuts} onClose={() => setShowShortcutHelp(false)} /> : null}

      {/* Remove from boundary (same modal pattern — no window.prompt) */}
      {removeFor ? (
        <ModalSheet labelledBy="remove-h" onClose={() => { setRemoveFor(null); setRemoveReason(''); }}>
          <h2 id="remove-h">Remove {removeFor.name} from boundary?</h2>
          <p>The asset will leave the assessment boundary. Auditors see the reason you record.</p>
          <label className="gp-field">
            <span>Reason <span aria-hidden="true" style={{ color: 'var(--band-red)' }}>*</span><span className="sr-only"> (required)</span></span>
            <textarea autoFocus aria-required="true" value={removeReason} onChange={(e) => setRemoveReason(e.currentTarget.value)} placeholder="e.g. moved to a separate enclave" />
          </label>
          <div className="gp-sheet-actions">
            <Button variant="secondary" onClick={() => { setRemoveFor(null); setRemoveReason(''); }}>Cancel</Button>
            <Button variant="primary" aria-disabled={!removeReason.trim() ? true : undefined} onClick={confirmRemove}>Remove</Button>
          </div>
        </ModalSheet>
      ) : null}
    </div>
  );
}

/** A11y-correct modal: ESC closes, focus trap, aria-modal, restores focus on unmount. */
function ModalSheet({ labelledBy, onClose, children }: { labelledBy: string; onClose: () => void; children: React.ReactNode }) {
  const sheetRef = useDialogMobileOnly<HTMLDivElement>(true);
  const prevFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    prevFocus.current = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const focusables = sheetRef.current.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0]; const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); prevFocus.current?.focus(); };
  }, [onClose]);
  return (
    <div className="gp-sheet-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={sheetRef} className="gp-sheet" aria-labelledby={labelledBy}>
        {children}
      </div>
    </div>
  );
}
